import { makeStyles } from "$components/mui-styles";
import React from "react";
import {
  type MessageDescriptor,
  type PrimitiveType,
  useIntl,
} from "react-intl";

export type ErrorMessage = MessageDescriptor & {
  values: Record<string, PrimitiveType>;
};
export interface HelperTextProps {
  children?: React.ReactNode;
  error?: string | ErrorMessage | null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "block",
    minHeight: "17px",
    transform: ({ children, error }: HelperTextProps) =>
      !children
        ? error
          ? "translateY(0)"
          : `translateY(-${theme.spacing(2)}px)`
        : "",
    transition: theme.transitions.create(["all"]),
    position: "relative",
    zIndex: ({ children, error }: HelperTextProps) => {
      if (children) {
        return 0;
      }
      return error ? "unset" : -1;
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const ensureErrorString = (
  error: string | ErrorMessage | null,
): string | undefined => {
  if (typeof error === "string") {
    return error;
  }

  return undefined;
};

/**
 * This component helps with displaying helper text
 * it takes into account if it is used only for showing errors
 * or if it is also responsible for showing helper text when there
 * is no error, the error always takes precedence.
 */
const HelperText = ({
  children = null,
  error = null,
}: HelperTextProps): React.ReactElement => {
  const classes = useStyles({ children, error });
  const { formatMessage } = useIntl();

  let errMsg: React.ReactNode;
  const errI18nMsg = error as ErrorMessage;
  if (errI18nMsg?.id) {
    errMsg = formatMessage(errI18nMsg, errI18nMsg.values);
  } else {
    errMsg = ensureErrorString(error);
  }

  return (
    <span className={`${classes.root} ${errMsg ? classes.error : ""}`}>
      {errMsg || children}
    </span>
  );
};

export default HelperText;
